// src/utils/avatarFraming.js
// All math in viewport pixels. The viewport is a circle of diameter V.
// frame = { zoom, tx, ty } where tx/ty are pan offsets from center (viewport px).

/** Scale so the image just covers the circle at zoom = 1. */
export function coverScale(naturalW, naturalH, V) {
  // Guard against zero/non-finite inputs (e.g. an image that failed to decode):
  // division would yield Infinity/NaN that cascades through frameScale, clampPan
  // and bakeRegion. Fall back to a neutral scale so the math stays finite.
  if (
    !Number.isFinite(naturalW) || naturalW <= 0 ||
    !Number.isFinite(naturalH) || naturalH <= 0 ||
    !Number.isFinite(V) || V <= 0
  ) {
    return 1;
  }
  return Math.max(V / naturalW, V / naturalH);
}

/** Effective scale at a given zoom. */
export function frameScale(naturalW, naturalH, V, zoom) {
  return coverScale(naturalW, naturalH, V) * zoom;
}

/** Clamp pan so the image always covers the circle (no gaps). tx/ty are VIEWPORT px. */
export function clampPan(tx, ty, naturalW, naturalH, V, zoom) {
  const s = frameScale(naturalW, naturalH, V, zoom);
  const mx = Math.max(0, (naturalW * s - V) / 2);
  const my = Math.max(0, (naturalH * s - V) / 2);
  return {
    tx: Math.min(mx, Math.max(-mx, tx)),
    ty: Math.min(my, Math.max(-my, ty)),
  };
}

/** Clamp zoom into the allowed range. */
export function clampZoom(zoom, min = 1, max = 3) {
  return Math.min(max, Math.max(min, zoom));
}

/**
 * Source rectangle (in image px) that maps to the visible circle.
 * tx/ty are VIEWPORT px (hence divided by s). Returns {sx, sy, sw, sh}.
 */
export function bakeRegion(naturalW, naturalH, V, frame) {
  const { zoom, tx, ty } = frame;
  const s = frameScale(naturalW, naturalH, V, zoom);
  const sw = V / s;
  const sh = V / s;
  const cx = naturalW / 2 - tx / s; // image point shown at viewport center
  const cy = naturalH / 2 - ty / s;
  return { sx: cx - sw / 2, sy: cy - sh / 2, sw, sh };
}

/**
 * Load a File as an orientation-corrected ImageBitmap (EXIF respected),
 * falling back to <img> on engines without createImageBitmap options.
 */
/**
 * Decode a File into an orientation-corrected source ({ source, width, height })
 * using createImageBitmap (EXIF-aware) with an <img> fallback.
 * Throws if the browser cannot decode the bytes (both decoders reject).
 */
async function decodeToSource(file) {
  // Remember why the primary decoder failed so the thrown error names both causes.
  let bitmapError;
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height };
    } catch (e) {
      bitmapError = e;
      /* fall through to the <img> decoder */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = () => rej(new Error('img element failed to decode'));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } catch (imgError) {
    const err = new Error(
      `browser could not decode image ` +
      `(createImageBitmap: ${bitmapError?.name || bitmapError?.message || 'n/a'}; ` +
      `img: ${imgError?.message || 'error'})`
    );
    err.cause = bitmapError || imgError;
    throw err;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Load a File as an orientation-corrected source for framing.
 *
 * Fast path: decode natively (JPEG/PNG/WebP/GIF/…). If that fails, the file is
 * most likely HEIC/HEIF (iPhone photos), which Chromium ships no decoder for —
 * so we lazily transcode it to JPEG and retry. `heic2any` is dynamically
 * imported ONLY on the failure path, so ordinary uploads never load the codec.
 *
 * Throws an `ImageDecodeError` when the file can be neither decoded nor
 * transcoded (a corrupt file, or a genuinely unsupported format like TIFF/RAW).
 */
export async function loadOrientedSource(file) {
  try {
    return await decodeToSource(file);
  } catch (decodeError) {
    let transcodeError;
    try {
      const { default: heic2any } = await import('heic2any');
      const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      const blob = Array.isArray(out) ? out[0] : out;
      const jpeg = new File([blob], 'converted.jpg', { type: 'image/jpeg' });
      return await decodeToSource(jpeg);
    } catch (e) {
      transcodeError = e;
    }
    // Neither native decode nor HEIC transcode worked — surface a rich, reportable
    // error so the real cause is visible (Sentry) instead of a swallowed blank.
    const err = new Error(
      `Image decode failed for "${file?.name || 'unknown'}" ` +
      `(type=${file?.type || 'unknown'}, size=${file?.size ?? '?'} bytes): ` +
      `${decodeError?.message || decodeError}`
    );
    err.name = 'ImageDecodeError';
    err.cause = decodeError;
    err.transcodeError = transcodeError;
    throw err;
  }
}

/**
 * Draw the framed region into an `out`×`out` canvas and resolve a named JPEG File.
 * Rejects if toBlob yields null. Sets `.preview` (object URL) because the in-form
 * avatar preview (AvatarPreview.jsx) renders `file.preview` for non-string values —
 * without it the avatar goes blank after Save.
 */
export async function bakeAvatarFile(source, naturalW, naturalH, V, frame, out = 400) {
  const { sx, sy, sw, sh } = bakeRegion(naturalW, naturalH, V, frame);
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, out, out);
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, out, out);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
  if (!blob) throw new Error('avatar bake failed');
  const f = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
  f.shouldRenew = true;
  f.preview = URL.createObjectURL(f);
  return f;
}
